import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { PublicCourseCard } from '../components/PublicCourseCard';
import { PublicBundleCard } from '../components/PublicBundleCard';
import { publicService, PublicCourse, PublicBundle } from '../services/publicService';
import { useTenant } from '../context/TenantContext';
import { applyShelfRule } from '../lib/catalogShelf';
import { uniformBadgeFlags } from '../lib/cardBadges';
import { TagFilterBar } from '../components/catalog/TagFilterBar';
import { ScopeSwitcher } from '../components/ScopeSwitcher';
import { useScope } from '../context/ScopeContext';
import { applyScope, applyScopeToBundles } from '../lib/catalogScope';
import { tagService } from '../services/tagService';
import { TagCategoryWithTags } from '../types';

export const PublicCatalog: React.FC = () => {
  const { tenantId, features } = useTenant();
  const { axes, selection, isResolving } = useScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState<PublicCourse[]>([]);
  const [bundles, setBundles] = useState<PublicBundle[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [tagCategories, setTagCategories] = useState<TagCategoryWithTags[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [coursesData, categoriesData] = await Promise.all([
          publicService.getPublicCourses(tenantId || undefined),
          publicService.getCategories(tenantId || undefined)
        ]);
        setCourses(coursesData);
        setCategories(['All', ...categoriesData]);
      } catch (error) {
        console.error('Failed to load courses:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    tagService
      .getPublicCategoriesWithTags(tenantId)
      .then(setTagCategories)
      .catch((err) => console.error('Failed to load tag categories', err));
  }, [tenantId]);

  useEffect(() => {
    if (!features.bundles) {
      setBundles([]);
      return;
    }
    let cancelled = false;
    publicService
      .getPublicBundles(tenantId || undefined)
      .then((result) => {
        if (!cancelled) setBundles(result);
      })
      // Best-effort: without bundles the shelf shows every course individually,
      // which is the pre-bundles catalogue rather than a broken page.
      .catch((err) => console.error('Failed to load bundles', err));
    return () => {
      cancelled = true;
    };
  }, [tenantId, features.bundles]);

  /* `q` and `category` off the URL, so a link elsewhere can land someone on a
     subset of the shelf — the Revise landing page points its free-papers CTAs
     at `/courses?q=Practice+Lab`.

     One-way, unlike `tag`: the URL drives the controls, the controls never
     write back. Round-tripping a text box through searchParams would rewrite
     history on every keystroke, and the effects would then fire on the user's
     own typing. Depending on the raw param STRING rather than on searchParams
     is what keeps them quiet — typing changes neither, so nothing re-fires,
     while arriving at `?q=…` (including from the announce bar while already on
     this page, which changes the URL without remounting) does. */
  const qParam = searchParams.get('q');
  const categoryParam = searchParams.get('category');

  useEffect(() => {
    setSearchQuery(qParam ?? '');
  }, [qParam]);

  useEffect(() => {
    setSelectedCategory(categoryParam ?? 'All');
  }, [categoryParam]);

  const selectedTagIds = useMemo(
    () => new Set(searchParams.getAll('tag')),
    [searchParams],
  );

  // The scope is the widest cut, so it lands BEFORE the shelf rule and every
  // filter — those all operate on what's in scope, never across it. A
  // signed-out visitor has no enrolments, so there is no owned exemption here.
  // Packs are matched against the UNSCOPED course list: a pack takes its
  // curriculum from its members, and deriving that from an already-narrowed
  // list would make every pack look like it matched.
  const scopedCourses = useMemo(
    () => applyScope(courses, axes, selection),
    [courses, axes, selection],
  );
  const scopedBundles = useMemo(
    () => applyScopeToBundles(bundles, courses, axes, selection),
    [bundles, courses, axes, selection],
  );

  // A scope category IS the page, so it never doubles as a filter — the same
  // rule the grouped catalogue applies to its groupBy/pivotBy categories.
  const scopeCategoryIds = useMemo(
    () => new Set(axes.map((a) => a.category.id)),
    [axes],
  );
  const filterableTagCategories = useMemo(
    () => tagCategories.filter((c) => !scopeCategoryIds.has(c.id)),
    [tagCategories, scopeCategoryIds],
  );

  // Counted inside the scope, so the bar hides tags that are dead ends for
  // the curriculum you're actually in (IEB carries no Practice Labs today).
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of scopedCourses) {
      for (const t of c.tags ?? []) {
        counts[t.id] = (counts[t.id] ?? 0) + 1;
      }
    }
    return counts;
  }, [scopedCourses]);

  const toggleTag = (tagId: string) => {
    const next = new URLSearchParams(searchParams);
    const all = next.getAll('tag');
    next.delete('tag');
    if (all.includes(tagId)) {
      all.filter((id) => id !== tagId).forEach((id) => next.append('tag', id));
    } else {
      [...all, tagId].forEach((id) => next.append('tag', id));
    }
    setSearchParams(next, { replace: true });
  };

  const clearTags = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('tag');
    setSearchParams(next, { replace: true });
  };

  // Bundles head the shelf only on the unfiltered view — they carry no
  // category or tags, so any active filter would silently drop them. The shelf
  // rule is bound to exactly that condition: members are folded into the pack
  // while the pack is visible, and released the moment it isn't, so a search or
  // filter can still find them.
  const isUnfiltered =
    searchQuery.trim() === '' && selectedCategory === 'All' && selectedTagIds.size === 0;
  const showBundles = isUnfiltered && scopedBundles.length > 0;

  const shelfCourses = useMemo(() => {
    if (!showBundles) return scopedCourses;
    // getPublicBundles only ever returns published rows.
    return applyShelfRule(
      scopedCourses,
      scopedBundles.map((b) => ({ published: true, courses: b.courses })),
      features.bundle_shelf_dedupe,
    );
  }, [scopedCourses, scopedBundles, showBundles, features.bundle_shelf_dedupe]);

  const filteredCourses = useMemo(() => {
    // Build category grouping for tag filters: OR within category, AND across categories.
    const tagToCategory = new Map<string, string>();
    for (const cat of tagCategories) {
      for (const t of cat.tags) tagToCategory.set(t.id, cat.id);
    }
    const selectedByCategory = new Map<string, Set<string>>();
    for (const tagId of selectedTagIds) {
      const catId = tagToCategory.get(tagId);
      if (!catId) continue;
      if (!selectedByCategory.has(catId)) selectedByCategory.set(catId, new Set());
      selectedByCategory.get(catId)!.add(tagId);
    }

    return shelfCourses.filter(course => {
      const matchesSearch =
        course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (course.instructorName ?? '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === 'All' || course.category === selectedCategory;

      let matchesTags = true;
      if (selectedByCategory.size > 0) {
        const courseTagIds = new Set((course.tags ?? []).map((t) => t.id));
        for (const [, tagSet] of selectedByCategory) {
          let any = false;
          for (const id of tagSet) {
            if (courseTagIds.has(id)) {
              any = true;
              break;
            }
          }
          if (!any) {
            matchesTags = false;
            break;
          }
        }
      }

      return matchesSearch && matchesCategory && matchesTags;
    });
  }, [shelfCourses, searchQuery, selectedCategory, selectedTagIds, tagCategories]);

  // Hold the skeleton until the scope has resolved too. While tag categories
  // are still loading `axes` is empty and applyScope is a no-op, so the page
  // would show the full unscoped catalogue — both curricula interleaved —
  // and then snap to the scoped one.
  if (isLoading || isResolving) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="pb-20"
    >
      {/* Header */}
      <div className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Browse Courses
            </h1>
            <p className="text-xl text-gray-500 max-w-2xl">
              Discover courses designed to transform your skills and accelerate
              your career growth.
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8 max-w-xl"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search courses..."
                className="w-full pl-12 pr-4 py-4 text-base bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-100 focus:border-primary-300 transition-all shadow-sm"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* The scope sits above everything it governs — the counts, the
            filters and the grid are all measured inside it. Renders nothing
            on tenants with no scope, or while only one curriculum is live. */}
        <ScopeSwitcher courses={courses} variant="bar" className="mb-8" />

        {/* Category Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="flex flex-wrap gap-2 mb-10"
        >
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${
                selectedCategory === cat
                  ? 'bg-gray-900 text-white border-gray-900 shadow-lg'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        <TagFilterBar
          // The scope categories ARE the page, so they never double as filters.
          categories={filterableTagCategories}
          selectedTagIds={selectedTagIds}
          onToggle={toggleTag}
          onClear={clearTags}
          tagCounts={tagCounts}
        />

        {/* Bundles — the headline product. Their member courses are folded in
            here rather than repeated in the grid below. */}
        {showBundles && (
          <section className="mb-14 rounded-2xl bg-paper-2 border border-paper-line p-6 sm:p-8">
            <h2 className="font-serif text-2xl font-bold text-gray-900 tracking-tight mb-6">
              {scopedBundles.length === 1 ? 'Bundle' : 'Bundles'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {scopedBundles.map((bundle, index) => (
                <motion.div
                  key={bundle.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * Math.min(index, 5) }}
                >
                  <PublicBundleCard bundle={bundle} canSell={features.sell_courses} />
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Results */}
        {filteredCourses.length > 0 ? (
          <>
            {showBundles && (
              <h2 className="font-serif text-2xl font-bold text-gray-900 tracking-tight mb-6">
                Individual courses
              </h2>
            )}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
          >
            {(() => {
              // Suppress badges that are identical across the tenant's whole
              // catalogue — computed from `courses`, not the filtered list, so
              // cards don't sprout badges as filters change.
              const { hideCategory, hideLevel } = uniformBadgeFlags(courses);
              return filteredCourses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * Math.min(index, 5) }}
                >
                  <PublicCourseCard course={course} hideCategory={hideCategory} hideLevel={hideLevel} />
                </motion.div>
              ));
            })()}
            </motion.div>
          </>
        ) : showBundles ? (
          // Every course is inside a bundle above — the page isn't empty, so
          // "no courses found" would contradict what the visitor can see.
          null
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-32 bg-white rounded-2xl border border-dashed border-gray-200"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-50 mb-6">
              <Search className="h-8 w-8 text-gray-300" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              No courses found
            </h3>
            <p className="text-gray-500 max-w-md mx-auto mb-8">
              {searchQuery
                ? `We couldn't find any courses matching "${searchQuery}"`
                : 'No courses available in this category yet.'}
            </p>
            <button
              onClick={() => {
                setSelectedCategory('All');
                setSearchQuery('');
                clearTags();
              }}
              className="px-6 py-3 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};
